import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import {
  Button,
  Checkbox,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  Input,
  Modal,
  PageBody,
  SegmentedControl,
  useToast,
  type SortState,
} from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./checklist.css";

/**
 * A private to-do list — jot something down, check it off. Separate from
 * Work on purpose: no assignee, no project, no priority, nobody else can
 * ever see it.
 */

interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
  createdAt: string;
  updatedAt: string;
}

type StatusFilter = "all" | "active" | "done";

function formatStamp(iso: string): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** `input[type=date]` values are local-midnight strings with no time
 *  component — compared against a full timestamp by day, not by instant, so
 *  picking "today" as the end of a range includes items added later today. */
function isOnOrAfter(iso: string, dateOnly: string): boolean {
  return iso.slice(0, 10) >= dateOnly;
}
function isOnOrBefore(iso: string, dateOnly: string): boolean {
  return iso.slice(0, 10) <= dateOnly;
}

export function Checklist() {
  usePageTitle("Checklist");
  const { toast } = useToast();
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<ChecklistItem | null>(null);

  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Undated by default: completed items sink to the bottom regardless of
  // whichever column is sorted, which is the point of the request — the
  // column sort still governs order *within* the active and the completed
  // groups.
  const [sort, setSort] = useState<SortState>({ key: "createdAt", dir: "desc" });

  useEffect(() => {
    api
      .get<{ items: ChecklistItem[] }>("/me/checklist")
      .then((res) => setItems(res.items))
      .catch(() => toast("Could not load your checklist.", { tone: "danger" }))
      .finally(() => setLoading(false));
  }, [toast]);

  const addItem = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      const text = draft.trim();
      if (!text || adding) return;

      setAdding(true);
      api
        .post<{ item: ChecklistItem }>("/me/checklist", { text })
        .then((res) => {
          setItems((prev) => [...prev, res.item]);
          setDraft("");
        })
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not add that item.", { tone: "danger" });
        })
        .finally(() => setAdding(false));
    },
    [draft, adding, toast],
  );

  const toggleDone = useCallback(
    (item: ChecklistItem) => {
      // Optimistic: a checkbox that waits for the network before ticking
      // reads as broken, not careful. The "done" stamp briefly shows the
      // moment of the click rather than the server's clock until the real
      // response lands just after — close enough that nobody notices the
      // difference, and it means the tick never waits on the network either.
      const optimisticStamp = new Date().toISOString();
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, done: !i.done, updatedAt: optimisticStamp } : i)),
      );
      api
        .patch<{ item: ChecklistItem }>(`/me/checklist/${item.id}`, { done: !item.done })
        .then((res) => {
          setItems((prev) => prev.map((i) => (i.id === item.id ? res.item : i)));
        })
        .catch(() => {
          setItems((prev) => prev.map((i) => (i.id === item.id ? item : i)));
          toast("Could not update that item.", { tone: "danger" });
        });
    },
    [toast],
  );

  const removeItem = useCallback(
    (item: ChecklistItem) => {
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      api.delete(`/me/checklist/${item.id}`).catch(() => {
        setItems((prev) => [...prev, item].sort((a, b) => (a.id < b.id ? -1 : 1)));
        toast("Could not remove that item.", { tone: "danger" });
      });
    },
    [toast],
  );

  const confirmRemove = useCallback(() => {
    if (!deleting) return;
    removeItem(deleting);
    setDeleting(null);
  }, [deleting, removeItem]);

  const completedCount = items.filter((i) => i.done).length;

  const clearCompleted = useCallback(() => {
    const remaining = items.filter((i) => !i.done);
    const cleared = items.filter((i) => i.done);
    setItems(remaining);
    api.delete("/me/checklist/completed").catch(() => {
      setItems((prev) => [...prev, ...cleared]);
      toast("Could not clear completed items.", { tone: "danger" });
    });
  }, [items, toast]);

  const visible = useMemo(() => {
    const filtered = items.filter((item) => {
      if (status === "active" && item.done) return false;
      if (status === "done" && !item.done) return false;
      if (search.trim() && !item.text.toLowerCase().includes(search.trim().toLowerCase())) return false;
      if (dateFrom && !isOnOrAfter(item.createdAt, dateFrom)) return false;
      if (dateTo && !isOnOrBefore(item.createdAt, dateTo)) return false;
      return true;
    });

    const direction = sort.dir === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      if (sort.key === "text") return direction * a.text.localeCompare(b.text);
      if (sort.key === "updatedAt") return direction * (a.updatedAt < b.updatedAt ? -1 : a.updatedAt > b.updatedAt ? 1 : 0);
      // createdAt, the default.
      return direction * (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0);
    });

    // Completed items always sink below active ones — a checked-off item
    // staying pinned above what's still outstanding is exactly the ordering
    // this was asked to stop doing. The column sort still decides order
    // within each of the two groups.
    return [...sorted.filter((i) => !i.done), ...sorted.filter((i) => i.done)];
  }, [items, status, search, dateFrom, dateTo, sort]);

  const hasFilters = status !== "all" || search.trim() !== "" || dateFrom !== "" || dateTo !== "";

  return (
    <PageBody
      title="Checklist"
      subtitle="Your own list — nobody else can see it."
      width="wide"
      actions={
        completedCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={clearCompleted}>
            Clear completed
          </Button>
        ) : undefined
      }
    >
      <form className="checklist__composer" onSubmit={addItem}>
        <Input
          aria-label="Add an item"
          placeholder="Add an item…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />
        <Button type="submit" loading={adding} disabled={!draft.trim()}>
          Add
        </Button>
      </form>

      {items.length > 0 && (
        <div className="checklist__filters">
          <SegmentedControl
            aria-label="Filter by status"
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: "All" },
              { value: "active", label: "Active" },
              { value: "done", label: "Done" },
            ]}
          />

          <label className="checklist__search">
            <Icon name="search" size={14} />
            <input
              type="search"
              placeholder="Search items…"
              value={search}
              aria-label="Search items"
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          <label className="checklist__date-field">
            <span>From</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="Added from" />
          </label>
          <label className="checklist__date-field">
            <span>To</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="Added to" />
          </label>
        </div>
      )}

      {loading ? null : items.length === 0 ? (
        <EmptyState icon="checklist" title="Nothing here yet" description="Add something above to get started." />
      ) : (
        <DataTable
          caption="Your checklist"
          density="compact"
          rows={visible}
          rowKey={(item) => item.id}
          sort={sort}
          onSortChange={setSort}
          empty={
            <EmptyState
              icon="search"
              title="Nothing matches"
              description={hasFilters ? "Try a different search or filter." : "Add something above to get started."}
            />
          }
          columns={[
            {
              key: "done",
              header: "",
              width: "36px",
              render: (item) => <Checkbox checked={item.done} onChange={() => toggleDone(item)} aria-label={`Mark "${item.text}" ${item.done ? "not done" : "done"}`} />,
            },
            {
              key: "text",
              header: "Item",
              sortable: true,
              rowHeader: true,
              render: (item) => (
                <span className={item.done ? "checklist__text is-done" : "checklist__text"}>{item.text}</span>
              ),
            },
            {
              key: "createdAt",
              header: "Created",
              sortable: true,
              width: "160px",
              render: (item) => <span className="checklist__stamp">{formatStamp(item.createdAt)}</span>,
            },
            {
              key: "updatedAt",
              header: "Completed",
              sortable: true,
              width: "160px",
              render: (item) => (
                <span className="checklist__stamp">{item.done ? formatStamp(item.updatedAt) : "—"}</span>
              ),
            },
            {
              key: "actions",
              header: "",
              width: "44px",
              align: "end",
              render: (item) => (
                <IconButton
                  icon={<Icon name="trash" size={13} />}
                  label={`Remove "${item.text}"`}
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleting(item)}
                />
              ),
            },
          ]}
        />
      )}

      <Modal open={!!deleting} onClose={() => setDeleting(null)} title="Remove this item?">
        <p className="checklist__delete-copy">
          {deleting ? `"${deleting.text}" will be removed.` : ""} This cannot be undone.
        </p>
        <div className="checklist__delete-actions">
          <Button variant="ghost" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemove}>
            Remove item
          </Button>
        </div>
      </Modal>
    </PageBody>
  );
}
